import React, { useRef } from 'react';
import styles from './DropdownOptionsList.module.scss';
import { type IOption } from '../models/IOption';
import DropdownOptionsListItem from './DropdownOptionsListItem/DropdownOptionsListItem';

export interface IDropdownOptionsList {
  id: string;
  options: IOption[];
  prioritizedOptions?: IOption[];
  selectedOption: IOption | null | undefined;
  onOptionClick: (option: IOption) => void;
  optionStyle?: 'OneLine' | 'TwoLine';
  listRef: React.RefObject<HTMLUListElement>;
  dropdownOpen: boolean;
}

export default function DropdownOptionsList({
  id,
  options,
  prioritizedOptions = [],
  selectedOption,
  onOptionClick,
  optionStyle = 'OneLine',
  listRef,
  dropdownOpen,
}: IDropdownOptionsList) {
  const optionRefs = useRef<Array<HTMLLIElement | null>>([]);

  const allOptions = [...prioritizedOptions, ...options];

  function handleKeyDown(event: React.KeyboardEvent<HTMLLIElement>, index: number) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextIndex = (index + 1) % allOptions.length;
      optionRefs.current[nextIndex]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevIndex = (index - 1 + allOptions.length) % allOptions.length;
      optionRefs.current[prevIndex]?.focus();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      onOptionClick(allOptions[index]);
    }
  }

  return (
    <ul
      className={!dropdownOpen ? styles.hideOptions : ''}
      id={`${id}-listbox`}
      role="listbox"
      ref={listRef}
      tabIndex={-1}
      aria-labelledby={`${id}-label`}
      aria-disabled={false}
    >
      {prioritizedOptions.length > 0 && (
        <>
          {prioritizedOptions?.map((option: IOption, index: number) => (
            <DropdownOptionsListItem
              id={id}
              option={option}
              key={`${id}-${index}`}
              index={index}
              isSelected={option.value === selectedOption?.value}
              onOptionClick={onOptionClick}
              optionStyle={optionStyle}
              optionRef={(el) => {
                optionRefs.current[index] = el;
              }}
              onKeyDown={handleKeyDown}
            ></DropdownOptionsListItem>
          ))}
          {options.length > 0 && <div className={styles.separator} aria-hidden="true" />}
        </>
      )}
      {options.map((option: IOption, index: number) => (
        <DropdownOptionsListItem
          id={id}
          option={option}
          key={`${id}-${index + prioritizedOptions.length}`}
          index={index + prioritizedOptions.length}
          isSelected={option.value === selectedOption?.value}
          onOptionClick={onOptionClick}
          optionStyle={optionStyle}
          optionRef={(el) => {
            optionRefs.current[index + prioritizedOptions.length] = el;
          }}
          onKeyDown={handleKeyDown}
        ></DropdownOptionsListItem>
      ))}
    </ul>
  );
}
